import { useEffect, useState } from "react";

interface QuizOption {
  id: string;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

interface QuizResult {
  id: string;
  title: string;
  subtitle: string;
  desc: string;
  tags: string[];
  image: string;
  accentColor: string;
  textAccent: string;
}

const quizOptions: QuizOption[] = [
  { id: "sea", label: "碧海蓝天，山海风光", icon: "ri-sailboat-line", color: "text-sky-600", bgColor: "bg-sky-50 hover:bg-sky-100", borderColor: "border-sky-200 hover:border-sky-400" },
  { id: "culture", label: "嘉庚风骨，人文底蕴", icon: "ri-ancient-pavilion-line", color: "text-amber-600", bgColor: "bg-amber-50 hover:bg-amber-100", borderColor: "border-amber-200 hover:border-amber-400" },
  { id: "food", label: "沙茶飘香，舌尖闽南", icon: "ri-restaurant-line", color: "text-red-500", bgColor: "bg-red-50 hover:bg-red-100", borderColor: "border-red-200 hover:border-red-400" },
  { id: "vibe", label: "浪漫宜居，活力之城", icon: "ri-heart-3-line", color: "text-rose-500", bgColor: "bg-rose-50 hover:bg-rose-100", borderColor: "border-rose-200 hover:border-rose-400" },
  { id: "music", label: "琴声悠扬，鼓浪余韵", icon: "ri-music-2-line", color: "text-violet-500", bgColor: "bg-violet-50 hover:bg-violet-100", borderColor: "border-violet-200 hover:border-violet-400" },
  { id: "night", label: "夜色迷人，灯火阑珊", icon: "ri-moon-line", color: "text-indigo-500", bgColor: "bg-indigo-50 hover:bg-indigo-100", borderColor: "border-indigo-200 hover:border-indigo-400" },
];

const quizResults: Record<string, QuizResult> = {
  sea: { id: "sea", title: "你是「山海漫游者」", subtitle: "Sea Wanderer", desc: "你热爱自然，向往自由。厦门的碧海蓝天、环岛路的海风、鼓浪屿的浪涛声，都是你灵魂最深处的呼唤。每一次面朝大海，都是与自己最好的对话。", tags: ["#环岛路骑行", "#鼓浪屿漫步", "#海边日落"], image: "https://public.readdy.ai/ai/img_res/42c06e0b5db007d995b14b1d0767497b.jpg", accentColor: "from-sky-400 to-cyan-500", textAccent: "text-sky-600" },
  culture: { id: "culture", title: "你是「人文探寻者」", subtitle: "Culture Explorer", desc: "你有一颗敏感而深邃的心，对历史与人文充满好奇。集美学村的嘉庚精神、沙坡尾的文艺气息、骑楼街的百年故事，都是你最想细细品味的厦门印记。", tags: ["#集美学村", "#沙坡尾文艺", "#骑楼老街"], image: "https://readdy.ai/api/search-image?query=Jimei%20School%20Village%20Xiamen%20beautiful%20architecture%20reflection%20in%20dragon%20boat%20lake%2C%20warm%20afternoon%20light%2C%20lush%20trees%2C%20peaceful%20cultural%20atmosphere%2C%20cinematic%20photography%2C%20ultra%20detailed&width=800&height=600&seq=result002&orientation=landscape", accentColor: "from-amber-400 to-orange-500", textAccent: "text-amber-600" },
  food: { id: "food", title: "你是「闽南美食家」", subtitle: "Minnan Foodie", desc: "你相信，了解一座城市最好的方式是品尝它的味道。沙茶面的浓郁、海蛎煎的鲜香、花生汤的甜蜜，每一口都是厦门最真实的烟火人间，让你念念不忘。", tags: ["#沙茶面", "#海蛎煎", "#花生汤配油条"], image: "https://readdy.ai/api/search-image?query=Xiamen%20street%20food%20market%20night%20scene%2C%20colorful%20food%20stalls%20with%20sa%20cha%20noodles%20oyster%20omelette%2C%20warm%20lantern%20lights%2C%20bustling%20atmosphere%2C%20steam%20rising%20from%20bowls%2C%20vibrant%20street%20food%20photography&width=800&height=600&seq=result003&orientation=landscape", accentColor: "from-red-400 to-orange-500", textAccent: "text-red-600" },
  vibe: { id: "vibe", title: "你是「浪漫生活家」", subtitle: "Romantic Dweller", desc: "你追求生活的质感与温度。厦门的宜居气候、滨海新城的现代活力、咖啡馆里的午后阳光，都是你心中理想生活的模样。这里，是你最想扎根的地方。", tags: ["#滨海新城", "#咖啡下午茶", "#宜居生活"], image: "https://readdy.ai/api/search-image?query=Xiamen%20modern%20coastal%20lifestyle%2C%20people%20relaxing%20at%20seaside%20cafe%20terrace%2C%20beautiful%20sea%20view%2C%20warm%20afternoon%20sunlight%2C%20lush%20greenery%2C%20romantic%20urban%20living%20atmosphere%2C%20ultra%20detailed%20photography&width=800&height=600&seq=result004&orientation=landscape", accentColor: "from-rose-400 to-pink-500", textAccent: "text-rose-600" },
  music: { id: "music", title: "你是「琴岛聆听者」", subtitle: "Piano Island Listener", desc: "你有一颗细腻而浪漫的灵魂，对美的感知超乎常人。鼓浪屿的钢琴声、海浪的节拍、老别墅里流淌的旋律，都是你与厦门之间最动人的共鸣。", tags: ["#鼓浪屿钢琴", "#音乐之岛", "#老别墅故事"], image: "https://readdy.ai/api/search-image?query=Gulangyu%20island%20Xiamen%20romantic%20scene%2C%20old%20colonial%20villa%20with%20piano%20music%20atmosphere%2C%20lush%20tropical%20garden%2C%20soft%20morning%20light%20filtering%20through%20trees%2C%20dreamy%20artistic%20photography%2C%20ultra%20detailed&width=800&height=600&seq=result005&orientation=landscape", accentColor: "from-violet-400 to-purple-500", textAccent: "text-violet-600" },
  night: { id: "night", title: "你是「夜城追光者」", subtitle: "Night City Chaser", desc: "你在夜晚才真正活过来。厦门的夜色迷人，滨海大道的灯火倒影、中山路的霓虹闪烁、海湾上的点点渔火，都是你最爱的厦门面孔，美得令人屏息。", tags: ["#厦门夜景", "#中山路夜市", "#海湾灯火"], image: "https://readdy.ai/api/search-image?query=Xiamen%20stunning%20night%20cityscape%2C%20glittering%20city%20lights%20reflecting%20on%20calm%20sea%20water%2C%20illuminated%20coastal%20road%2C%20vibrant%20nightlife%2C%20deep%20blue%20sky%2C%20long%20exposure%20photography%2C%20cinematic%20aerial%20view&width=800&height=600&seq=result006&orientation=landscape", accentColor: "from-indigo-400 to-blue-600", textAccent: "text-indigo-600" },
};

interface QuizPageProps {
  visible: boolean;
  onNext: () => void;
  onPrev: () => void;
}

export default function QuizPage({ visible, onNext, onPrev }: QuizPageProps) {
  const [entered, setEntered] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [resultVisible, setResultVisible] = useState(false);
  const [clickedId, setClickedId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntered(true), 80);
      return () => clearTimeout(t);
    } else {
      setEntered(false);
      setShowResult(false);
      setResultVisible(false);
      setSelectedId(null);
      setClickedId(null);
    }
  }, [visible]);

  const handleSelect = (id: string) => {
    if (showResult) return;
    setClickedId(id);
    setSelectedId(id);
    setTimeout(() => {
      setShowResult(true);
      setTimeout(() => setResultVisible(true), 60);
    }, 500);
  };

  const handleReset = () => {
    setResultVisible(false);
    setTimeout(() => {
      setShowResult(false);
      setSelectedId(null);
      setClickedId(null);
    }, 400);
  };

  const result = selectedId ? quizResults[selectedId] : null;

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex flex-col">
      <div className="absolute top-0 right-0 w-72 md:w-96 h-72 md:h-96 rounded-full bg-amber-100/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-60 md:w-80 h-60 md:h-80 rounded-full bg-sky-100/40 blur-3xl pointer-events-none" />

      {/* Top Nav */}
      <nav className="relative z-30 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <img src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png" alt="Logo" className="h-8 md:h-9 w-auto object-contain" />
        <div className="hidden md:flex items-center gap-6">
          {["山海", "人文", "美食", "活力"].map((item) => (
            <span key={item} className="text-xs tracking-widest text-gray-400 hover:text-gray-700 cursor-pointer transition-colors whitespace-nowrap">{item}</span>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 md:px-6 pb-8 md:pb-10 overflow-y-auto">

        {/* Question Panel */}
        {!showResult && (
          <div className={`w-full max-w-3xl transition-all duration-700 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-5 md:mb-10">
              <div className="inline-flex items-center gap-2 bg-white border border-gray-100 rounded-full px-3 md:px-4 py-1 md:py-1.5 mb-3 md:mb-5">
                <span className="text-gray-400 text-xs tracking-[0.2em]">06 / INTERACTIVE</span>
              </div>
              <h2
                className="text-3xl md:text-5xl font-light text-gray-900 mb-2 md:mb-3"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                你的厦门印象
              </h2>
              <p className="text-gray-400 text-xs md:text-sm tracking-wider">你最爱厦门的什么？选一个，看看你是哪种厦门人</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
              {quizOptions.map((opt, i) => (
                <button
                  key={opt.id}
                  onClick={() => handleSelect(opt.id)}
                  className={`relative flex items-center gap-2 md:gap-3 px-3 md:px-5 py-3 md:py-4 rounded-xl md:rounded-2xl border-2 transition-all duration-300 cursor-pointer text-left group
                    ${clickedId === opt.id
                      ? `${opt.bgColor.split(" ")[0]} ${opt.borderColor.split(" ")[1] || opt.borderColor} scale-95`
                      : `bg-white ${opt.borderColor} ${opt.bgColor}`
                    }`}
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <div className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl flex-shrink-0 ${opt.bgColor.split(" ")[0]} transition-transform duration-300 group-hover:scale-110`}>
                    <i className={`${opt.icon} ${opt.color} text-base md:text-lg`} />
                  </div>
                  <span className="text-gray-700 text-xs md:text-sm font-medium leading-snug">{opt.label}</span>
                  {clickedId === opt.id && (
                    <div className="absolute inset-0 rounded-xl md:rounded-2xl border-2 border-current animate-ping opacity-30" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Result Card */}
        {showResult && result && (
          <div className={`w-full max-w-2xl transition-all duration-500 ${resultVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-6"}`}>
            <div className="bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-gray-100">
              <div className="relative h-40 md:h-52 overflow-hidden">
                <img src={result.image} alt={result.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${result.accentColor}`} />
                <div className="absolute top-3 left-3 md:top-4 md:left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-2.5 md:px-3 py-1 rounded-full">
                    你的专属印象
                  </span>
                </div>
                <div className="absolute bottom-3 left-4 md:bottom-4 md:left-5">
                  <p className="text-white/70 text-xs mb-0.5">{result.subtitle}</p>
                  <h3 className="text-white text-xl md:text-2xl font-semibold" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                    {result.title}
                  </h3>
                </div>
              </div>

              <div className="p-4 md:p-6">
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-4 md:mb-5">{result.desc}</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-6">
                  {result.tags.map((tag) => (
                    <span key={tag} className={`text-xs px-2.5 md:px-3 py-1 rounded-full bg-gray-50 border border-gray-100 ${result.textAccent} font-medium`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-2.5 md:py-3 rounded-xl border border-gray-200 text-gray-600 text-xs md:text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    再选一次
                  </button>
                  <button
                    onClick={onNext}
                    className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-2.5 md:py-3 rounded-xl bg-gradient-to-r ${result.accentColor} text-white text-xs md:text-sm font-medium hover:opacity-90 transition-all cursor-pointer whitespace-nowrap`}
                  >
                    继续探索
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-3 md:mt-4 text-gray-400 text-xs">
              <i className="ri-share-line" />
              <span>分享你的厦门印象给朋友</span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:border-gray-400 transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      {showResult && (
        <button
          onClick={onNext}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700 transition-all cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      )}

      {/* Page indicator */}
      <div className="absolute bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`rounded-full transition-all duration-300 ${i === 5 ? "w-6 h-1.5 bg-gray-700" : "w-1.5 h-1.5 bg-gray-300"}`} />
        ))}
      </div>
    </div>
  );
}
